import { useEffect, useRef, useState } from "react";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useParams } from "react-router-dom";
import { useToast } from 'hooks/useToast';
import { Button } from "components/Button";
import { iconArrowRight, iconTrash, iconX } from "assets/icons";

interface Property {
  name?: string,
  value?: string,
  action?: () => void,
}

export interface Track {
  trackId: string;
  eventId: string;
  name: string;
  description: string;
  color: string;
  colorText: string;
  insertDate: string;
  updateDate: string;
}

interface TrackRowProps {
  name: string;
  description: string;
  color: string;
  insertDate: string;
  actions: JSX.Element[];
}

export const useTracksTable = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const initialTableFilters: FilterProps = {};
  
  const [properties, setProperties] = useState<Property[]>();
  const [tracks, setTracks] = useState<Track[]>();
  const [trackRows, setTrackRows] = useState<TrackRowProps[]>([]);
  const [tableFilters, setTableFilters] = useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [tracksPerPage, setTracksPerPage] = useState<number>(10);
  const [tracksCount, setTracksCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  
  const searchTextFormRef = useRef<any>(null);
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();
  const { setToast } = useToast();

  /* HOOKS */ 
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    getTracks();
  }, [eventId, pageNumber, tracksPerPage, searchText, tableFilters]);

  const rowActionButton = (route: string) => {
    return (
      <Button
        label=""
        typeBtn="default"
        icon={iconArrowRight}
        onClick={() => {}}
        block={false}
      />
    );
  };

  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const searchTextValue = formData.get('searchText') as string;
    setSearchText(searchTextValue);
    setPageNumber(1);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target;
    setTableFilters(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
      },
    }));
    setPageNumber(1);
  };

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters(prev => ({
      ...prev,
      [filterName]: {
        ...prev[filterName],
        value: '',
      },
    }));
    setPageNumber(1);
  };

  const handleSearchTextRemove = (): void => {
    setSearchText('');
    setPageNumber(1);
  };

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setTracksPerPage(Number(event.target.value));
    setPageNumber(1);
  };

  const getTracks = async () => {
    setLoading(true);
    setError(false);

    try {
      const query = `
        query BackofficeTracks($filter: TrackFilterInput, $pagination: PaginationInput) {
          backofficeTracks(filter: $filter, pagination: $pagination) {
            trackId
            eventId
            name
            description
            color
            colorText
            insertDate
            updateDate
          }
        }
      `;

      const variables = {
        filter: {
          eventId,
          searchText: searchText || undefined,
        },
        pagination: {
          offset: (pageNumber - 1) * tracksPerPage,
          limit: tracksPerPage,
        },
      };

      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: 'Erro ao buscar trilhas',
        useNewApi: true,
      });

      const tracksData = response.data.backofficeTracks;
      setTracks(tracksData);
      setTracksCount(tracksData.length); // Note: This should be a separate count query

      const rows = tracksData.map((track: Track) => ({
        name: track.name,
        description: track.description || '-',
        color: (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <div 
              style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: track.color,
                borderRadius: '4px',
                border: '1px solid #ddd'
              }} 
            />
            <span>{track.color}</span>
          </div>
        ),
        insertDate: new Date(track.insertDate).toLocaleDateString('pt-BR'),
        actions: [
          <Button
            key="edit"
            label=""
            typeBtn="default"
            icon={iconArrowRight}
            onClick={() => {}}
            block={false}
          />
        ],
      }));

      setTrackRows(rows);

      // Set properties for cards view
      setProperties([
        {
          name: "Total de trilhas",
          value: tracksData.length.toString(),
        },
      ]);

    } catch (error) {
      console.error('Error fetching trilhas:', error);  
      setError(true);
      setToast({
        type: "error",
        message: "Erro ao buscar trilhas.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    properties,
    trackRows,
    tracksCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    tracksPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
    tracks,
    getTracks,
  };
}; 